<script>
import lazyLoadComponent from '@router/lazyload-component'
import BaseButtonDialog from '@components/BaseButtonDialog'
import { getHotelGoodsTags, createHotelGoodsTag } from '@/src/apis/hotelGoodsTag'

const TAG_COLOR_OPTIONS = [{ label: '預設', value: '' }, { label: '亮綠', value: 'success' }, { label: '灰色', value: 'info' }, { label: '黃色', value: 'warning' }, { label: '紅色', value: 'danger' }]

export default {
  components: {
    BaseButtonDialog,
    GoodsTagManageListItem: () => lazyLoadComponent(import('./GoodsTagManageListItem.vue')),
  },
  props: {
    dialogVisible: {
      type: Boolean,
    },
  },
  data() {
    return {
      tagList: [],
      newTag: { name: '', color: '' },
      tagColorOptions: TAG_COLOR_OPTIONS,
      tryAdd: false,
      show: this.dialogVisible,
      activeItemIndex: '',
    }
  },
  computed: {
    hasTagList() {
      return this.tagList.length > 0
    },
  },
  watch: {
    dialogVisible(val) {
      this.show = val
    },
  },
  methods: {
    async tryToFetchList() {
      try {
        const tagsRes = await getHotelGoodsTags()
        this.tagList = Array.isArray(tagsRes) ? tagsRes : tagsRes?.items || []
      } catch (error) {
        console.error(error)
      }
    },
    async tryToAdd() {
      try {
        if (this.tryAdd) return
        if (!this.newTag.name.trim()) {
          this.$message({ message: '請輸入標籤名稱', type: 'warning' })
          return
        }
        this.tryAdd = true
        const tagId = crypto.randomUUID()
        const tagPayload = { name: this.newTag.name.trim(), color: this.newTag.color }
        await createHotelGoodsTag(tagId, tagPayload)
        this.newTag = { name: '', color: '' }
        this.tryToFetchList()
      } catch (error) {
        console.error(error)
      } finally {
        this.tryAdd = false
      }
    },
    closeDialog() {
      this.$emit('update:dialogVisible', false)
      this.$emit('closed')
      this.activeItemIndex = ''
    },
    updateActiveIndex(index) {
      this.activeItemIndex = index
    },
  },
}
</script>

<template>
  <BaseButtonDialog :only-dialog="true" title="標籤管理" :dialog-visible.sync="show" :destroy="true" width="58%" floating @closed="closeDialog" @dialog-opened="tryToFetchList">
    <el-form class="el-form-item--normal-margin" @submit.prevent.native>
      <!-- 新增標籤區塊 -->
      <el-form-item>
        <!-- 顏色選擇：5 個固定 type 按鈕 -->
        <div :class="$style.colorRow">
          <span :class="$style.colorLabel">顏色：</span>
          <el-tag
            v-for="opt in tagColorOptions"
            :key="opt.value || 'none'"
            :type="opt.value || undefined"
            size="medium"
            :effect="newTag.color === opt.value ? 'dark' : 'plain'"
            :class="$style.colorBtn"
            @click="newTag.color = opt.value"
            >{{ opt.label }}</el-tag
          >
        </div>
        <el-input v-model.trim="newTag.name" placeholder="建立新標籤名稱" clearable :class="$style.createInput" class="isStyleButton" @keydown.enter.prevent="tryToAdd">
          <el-button slot="append" v-loading="tryAdd" type="primary" class="is-round" @click="tryToAdd">
            新增
          </el-button>
        </el-input>
        <!-- 新增時顏色預覽 -->
        <div v-if="newTag.name" :class="$style.previewRow">
          <span style="color: #909399; font-size: 12px;">預覽：</span>
          <el-tag :type="newTag.color || undefined" size="mini">{{ newTag.name }}</el-tag>
        </div>
      </el-form-item>

      <!-- 標籤列表 -->
      <div :class="$style.list">
        <el-divider class="my-2" />
        <transition-group v-if="hasTagList" name="el-zoom-in-center">
          <GoodsTagManageListItem
            v-for="(tag, index) in tagList"
            :key="`hotel-goods-tag-${tag.id}`"
            :default-value="tag"
            :item-index="index"
            :current-active-index="activeItemIndex"
            @update="updateActiveIndex"
            @refresh="tryToFetchList"
          />
        </transition-group>
        <div v-else style="text-align: center; color: darkgray; line-height: 5em;">
          暫無資料顯示
        </div>
      </div>
    </el-form>

    <div class="py-2">
      <el-divider class="my-2" />
      <p style="color: #3F3A2E;">提醒您：移除標籤不會影響已綁定該標籤的商品</p>
    </div>
  </BaseButtonDialog>
</template>

<style lang="scss" module>

.list {
  overflow: auto;
  max-height: 30vh;
  margin-bottom: 20px;

  &::-webkit-scrollbar {
    display: block;
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #d8d8d8;
  }
}

.colorSelect {
  width: 110px;
}

.colorRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.colorLabel {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.colorBtn {
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.85;
  }
}

.previewRow {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding-left: 4px;
}
</style>
